import { QueryClient, QueryFunction } from "@tanstack/react-query";
import { getApiUrl, getMediaUrl } from "./apiConfig";

// Store for the access token getter function
let getAccessTokenFn: (() => Promise<string | null>) | null = null;

export function setAccessTokenGetter(fn: () => Promise<string | null>) {
  console.log('Setting access token getter function');
  getAccessTokenFn = fn;
}

export function hasAccessTokenGetter(): boolean {
  return getAccessTokenFn !== null;
}

async function throwIfResNotOk(res: Response) {
  if (!res.ok) {
    try {
      // Clone the response to avoid consuming it
      const clonedRes = res.clone();
      const errorData = await clonedRes.json();
      throw new Error(errorData.message || res.statusText);
    } catch (parseError) {
      // If JSON parsing fails, fall back to text
      const text = await res.text() || res.statusText;
      throw new Error(text, { cause: parseError });
    }
  }
}

export async function mediaRequest(
  method: string,
  url: string,
  data?: unknown | undefined,
): Promise<Response> {
  const isFormData = data instanceof FormData;
  const isFile = data instanceof File;
  // Use the media configuration to get the full URL
  const fullUrl = getMediaUrl(url);
  // Prepare headers
  const headers: Record<string, string> = {};
  if (data && !isFormData && !isFile) {
    headers["Content-Type"] = "application/json";
  }
  // If uploading a file directly, set Content-Type to file's type
  if (isFile) {
    headers["Content-Type"] = (data as File).type || "application/octet-stream";
  }
  
  // Add Authorization header if token is available
  if (getAccessTokenFn) {
    try {
      const token = await getAccessTokenFn();
      console.log('Media Request - Token retrieved:', token ? 'Token present' : 'No token');
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }
    } catch (error) {
      console.warn('Failed to get access token for media request:', error);
    }
  } else {
    console.warn('No access token getter function available for media request');
  }
  
  const res = await fetch(fullUrl, {
    method,
    headers,
    body: isFormData ? data : isFile ? data : (data ? JSON.stringify(data) : undefined),
  });

  await throwIfResNotOk(res);
  return res;
}

export async function apiRequest(
  method: string,
  url: string,
  data?: unknown | undefined,
): Promise<Response> {
  const isFormData = data instanceof FormData;
  const isFile = data instanceof File;
  // Use the API configuration to get the full URL
  const fullUrl = getApiUrl(url);
  // Prepare headers
  const headers: Record<string, string> = {};
  if (data && !isFormData && !isFile) {
    headers["Content-Type"] = "application/json";
  }
  // If uploading a file directly, set Content-Type to file's type
  if (isFile) {
    headers["Content-Type"] = (data as File).type || "application/octet-stream";
  }
  
  // Add Authorization header if token is available
  if (getAccessTokenFn) {
    try {
      const token = await getAccessTokenFn();
      console.log('API Request - Token retrieved:', token ? 'Token present' : 'No token');
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }
    } catch (error) {
      console.warn('Failed to get access token for API request:', error);
    }
  } else {
    console.warn('No access token getter function available for API request');
  }
  
  const res = await fetch(fullUrl, {
    method,
    headers,
    body: isFormData ? data : isFile ? data : (data ? JSON.stringify(data) : undefined),
  });

  await throwIfResNotOk(res);
  return res;
}

type UnauthorizedBehavior = "returnNull" | "throw";
export const getQueryFn: <T>(options: {
  on401: UnauthorizedBehavior;
}) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior }) =>
  async ({ queryKey }) => {
    // Use the API configuration to get the full URL
    const fullUrl = getApiUrl(queryKey[0] as string);
    
    // Prepare headers
    const headers: Record<string, string> = {};
    
    // Add Authorization header if token is available
    if (getAccessTokenFn) {
      try {
        const token = await getAccessTokenFn();
        console.log('Query - Token retrieved:', token ? 'Token present' : 'No token');
        if (token) {
          headers["Authorization"] = `Bearer ${token}`;
        }
      } catch (error) {
        console.warn('Failed to get access token for query:', error);
      }
    } else {
      console.warn('No access token getter function available for query');
    }
    
    const res = await fetch(fullUrl, {
      headers,
    });

    if (unauthorizedBehavior === "returnNull" && res.status === 401) {
      return null;
    }

    await throwIfResNotOk(res);
    return await res.json();
  };

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "throw" }),
      refetchInterval: false,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
      gcTime: Infinity,
      retry: false,
    },
    mutations: {
      retry: false,
    },
  },
});


